import type { ReactNode } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useResolvedColors } from '@/lib/useResolvedColor';
import { QuarterRangeSlider } from './QuarterRangeSlider';
import { ChartSummary } from './ChartSummary';
import { ChartLegend } from './ChartLegend';
import { ChartTooltip } from './ChartTooltip';
import { QuarterTick, useQuarterWindow, profileChartStyles as s } from './ProfileSection';

export interface ContributionSeries {
  key: string;
  label: string;
  /** Colour token. */
  color: string;
}

export interface ContributionPoint {
  label: string;
  /** The change the parts add up to. */
  total: number;
  [part: string]: string | number;
}

export interface ContributionChartProps {
  /** Oldest → newest; each part keyed by a series key. */
  points: ContributionPoint[];
  series: ContributionSeries[];
  totalLabel: string;
  /** Formats contributions and totals ("+2.1%"). */
  format: (value: number) => string;
  yTitle: string;
  /** Sentence above the plot, for the last visible point. */
  summary?: (point: ContributionPoint) => ReactNode;
}

const LABEL_ALL_MAX = 12;

/**
 * What a change is made of, period by period: the parts stacked around zero
 * (positive parts up, negative down) with the total as a dot. For growth
 * decompositions — revenue into subscribers / price / mix, and the like.
 */
export function ContributionChart({ points: allPoints, series, totalLabel, format, yTitle, summary }: ContributionChartProps) {
  const [[lo, hi], setRange] = useQuarterWindow(allPoints.length);
  const points = allPoints.slice(lo, hi + 1);
  const colors = useResolvedColors([...series.map((x) => x.color), 'var(--text-primary)']);
  const totalColor = colors[series.length];
  const showAllLabels = points.length <= LABEL_ALL_MAX;
  const last = points[points.length - 1];

  return (
    <>
      {summary && last && <ChartSummary className={s.summary}>{summary(last)}</ChartSummary>}
      <div className={s.body}>
        {allPoints.length > 1 && (
          <QuarterRangeSlider
            count={allPoints.length}
            value={[lo, hi]}
            onChange={setRange}
            labels={allPoints.map((p) => p.label)}
          />
        )}
        {points.length ? (
          <>
            <div className={s.plot}>
              <span className={`${s.yAxisTitle} ${s.yAxisTitleLeft}`}>{yTitle}</span>
              <ResponsiveContainer width="100%" height={360}>
                <ComposedChart data={points} stackOffset="sign" barCategoryGap="28%" margin={{ top: 28, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid stroke="var(--border-subtle)" vertical={false} />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} interval={0} height={46} tick={QuarterTick} />
                  <YAxis className={s.axis} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => format(Number(v))} />
                  <ReferenceLine y={0} stroke="var(--border-strong)" strokeWidth={1.5} />
                  <Tooltip
                    cursor={{ fill: 'var(--bg-hover)', opacity: 0.5 }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const p = payload[0].payload as ContributionPoint;
                      return (
                        <ChartTooltip
                          title={label}
                          rows={series.map((x, i) => ({ label: x.label, value: format(Number(p[x.key])), color: colors[i] }))}
                          totals={[{ label: totalLabel, value: format(p.total) }]}
                        />
                      );
                    }}
                  />
                  {series.map((x, i) => (
                    <Bar key={x.key} dataKey={x.key} stackId="parts" fill={colors[i]} isAnimationActive={false} />
                  ))}
                  {/* The total as a dot, no line: each period is its own sum */}
                  <Line
                    dataKey="total"
                    stroke="none"
                    dot={{ r: 5, fill: totalColor, stroke: 'var(--bg-card)', strokeWidth: 2 }}
                    activeDot={{ r: 6 }}
                    isAnimationActive={false}
                  >
                    <LabelList
                      dataKey="total"
                      content={(props) => {
                        const { x, y, value, index } = props as { x: number; y: number; value: number; index: number };
                        if (!showAllLabels && index !== points.length - 1) return null;
                        return (
                          <text x={x} y={value < 0 ? y + 20 : y - 12} textAnchor="middle" className={s.valueLabel}>
                            {format(value)}
                          </text>
                        );
                      }}
                    />
                  </Line>
                </ComposedChart>
              </ResponsiveContainer>
              <span />
            </div>
            <ChartLegend
              items={[
                ...series.map((x, i) => ({ label: x.label, color: colors[i] })),
                { label: totalLabel, color: totalColor, shape: 'dot' as const },
              ]}
            />
          </>
        ) : (
          <div className={s.empty}>Not enough history to break down</div>
        )}
      </div>
    </>
  );
}
